import { useEffect, useMemo, useState } from "react";

import { ApolloError } from "@apollo/client";
import { Box, Flex, IconButton, useDisclosure } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { generatePath, useNavigate } from "react-router";

import { ShelfChangeStatus } from "__graphql__/types";
import { PageName, ShelfChangeAction, ShelfChangeProgressState } from "analytics/events";
import { routes } from "config/routes";
import { Page } from "shared/components/Page";
import { SpinnerModal } from "shared/components/SpinnerModal";
import { useAnalytics } from "shared/hooks/useAnalytics";
import { useCustomToast } from "shared/hooks/useCustomToast";
import { useScan } from "shared/hooks/useScan/useScan";
import { AscDscIcon } from "ui/AscDscIcon/AscDscIcon";
import { ScanIcon } from "ui/Icons/Icons";
import { NavigationHeader } from "ui/NavigationHeader/NavigationHeader";
import { TitleM, TitleS } from "ui/Typography/Typography";

import { EmptyState } from "../components/EmptyState";
import { ItemNotFoundModal } from "../components/ItemNotFoundModal";
import { ShelfAlreadyAssignedModal } from "../components/ShelfAlreadyAssignedModal";
import { ShelfChangeCard } from "../components/ShelfChangeCard";
import {
  useGetShelfChangeProposalsLazyQuery,
  useStartShelfChangeProposalMutation,
} from "../queries/shelfChanges.generated";

export const SORT_ORDER = {
  ASC: "asc" as const,
  DESC: "desc" as const,
};

export type SortOrder = (typeof SORT_ORDER)[keyof typeof SORT_ORDER];

export function ShelfChangesPage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { showToastUI } = useCustomToast();
  const { sendSegmentTrackEvent } = useAnalytics();

  const [sortOrder, setSortOrder] = useState<SortOrder>(SORT_ORDER.ASC);

  const {
    onOpen: onOpenItemNotFoundModal,
    isOpen: isItemNotFoundModalOpen,
    onClose: onCloseItemNotFoundModal,
  } = useDisclosure();

  const {
    onOpen: onOpenShelfAlreadyAssignedModal,
    isOpen: isShelfAlreadyAssignedModalOpen,
    onClose: onCloseShelfAlreadyAssignedModal,
  } = useDisclosure();

  const [getProposals, { data, loading, refetch }] = useGetShelfChangeProposalsLazyQuery({
    fetchPolicy: "network-only",
    nextFetchPolicy: "network-only",
    variables: { shelfOrder: sortOrder },
    onError: (error) => {
      showToastUI({
        id: "shelf_change_error_toast_id",
        title: "hooks.use-custom-toast.error-title",
        description: error.message,
      });
    },
  });

  const [startShelfChangeProposal, { loading: isStarting }] = useStartShelfChangeProposalMutation();

  useEffect(() => {
    getProposals({ variables: { shelfOrder: sortOrder } });
  }, [sortOrder, getProposals]);

  const handleSortToggle = () => {
    const newOrder = sortOrder === SORT_ORDER.ASC ? SORT_ORDER.DESC : SORT_ORDER.ASC;
    setSortOrder(newOrder);
  };

  const handleItemNotFoundModalClose = () => {
    onCloseItemNotFoundModal();
    refetch();
  };

  const handleShelfAlreadyAssignedModalClose = () => {
    onCloseShelfAlreadyAssignedModal();
    refetch();
  };

  const groupedProposals = useMemo(() => {
    if (!data?.getShelfChangeProposals?.proposals) return {};
    return data.getShelfChangeProposals.proposals.reduce<
      Record<string, typeof data.getShelfChangeProposals.proposals>
    >((acc, proposal) => {
      const dueDateString = proposal.dueDate ?? "";
      if (!dueDateString) return acc;
      const date = new Date(dueDateString).toISOString().split("T")[0];
      if (!acc[date]) acc[date] = [];
      acc[date].push(proposal);
      return acc;
    }, {});
  }, [data]);

  const isInEmptyState = !Object.keys(groupedProposals).length;

  const formatDueDate = (dateString: string) => {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = { weekday: "long", day: "2-digit", month: "long" };
    const [weekday, day, month] = date.toLocaleDateString("en-GB", options).split(" ");
    return `${weekday}, ${day} ${month}`;
  };

  const showErrorToast = (description?: string) => {
    showToastUI({
      id: "shelf_change_error_toast_id",
      title: "hooks.use-custom-toast.error-title",
      description,
    });
  };

  const handleScan = async (scannedValue?: string) => {
    if (isStarting) return;
    const allProposals = Object.values(groupedProposals).flat();
    const proposalsForShelf = allProposals.filter((p) => p.shelfFrom === scannedValue);

    const openProposal = proposalsForShelf.find((p) => p.status === ShelfChangeStatus.open);

    try {
      if (!openProposal) {
        onOpenItemNotFoundModal();
        sendSegmentTrackEvent("inAppMessageShown", {
          screen_name: PageName.INVENTORY_SHELF_CHANGES_PAGE,
          component_name: "item_not_found",
          component_content: scannedValue,
        });
        return;
      }

      await startShelfChangeProposal({ variables: { proposalId: openProposal.id } });
      sendSegmentTrackEvent("shelfChangeStateUpdated", {
        state: ShelfChangeProgressState.Start,
        action: ShelfChangeAction.Scan,
        shelf_change_id: openProposal.id,
        product_sku: openProposal.sku ?? "",
        product_name: openProposal.productName ?? "",
        from_shelf_id: openProposal.shelfFrom ?? "",
        to_shelf_id: openProposal.shelfTo ?? "",
      });
      const url = generatePath(routes.inventory.shelfChanges.detail, {
        proposalId: openProposal.id,
      });
      navigate(url);
    } catch (error) {
      if (error instanceof ApolloError) {
        const gqlError = error.graphQLErrors?.[0];

        if (gqlError?.extensions?.code === "SHELF_PROPOSAL_NOT_OPEN") {
          onOpenShelfAlreadyAssignedModal();
          sendSegmentTrackEvent("inAppMessageShown", {
            screen_name: PageName.INVENTORY_SHELF_CHANGES_PAGE,
            component_name: "shelf_assigned",
            component_content: openProposal?.id,
          });
          return;
        }
        showErrorToast(gqlError?.message);
        return;
      }
      showErrorToast();
    }
  };

  useScan({ onScan: handleScan });

  return (
    <Page isFull isBgGrey h="100%">
      <NavigationHeader
        onClickGoBack={() => {
          navigate(routes.inventory.root);
        }}
        title={intl.formatMessage({
          id: "pages.inventory.shelf-changes.title",
        })}
        rightElement={
          isInEmptyState ? undefined : (
            <Flex align="center">
              <IconButton
                size="xs"
                variant="ghost"
                aria-label="Change sort order"
                icon={<AscDscIcon height="24px" width="24px" sortState={sortOrder} />}
                onClick={handleSortToggle}
              />
            </Flex>
          )
        }
      />
      <SpinnerModal isOpen={loading || isStarting} />
      {isInEmptyState ? (
        <EmptyState />
      ) : (
        <>
          <Flex w="100%" h="100%" flex="1" direction="column" overflowY="scroll" mb="80px">
            {Object.entries(groupedProposals).map(([dueDate, proposals]) => (
              <Flex direction="column" key={dueDate}>
                <Box px="s200" py="s300">
                  <TitleM>{formatDueDate(dueDate)}</TitleM>
                </Box>
                <Flex direction="column" gap="s100">
                  {proposals.map((p) => (
                    <ShelfChangeCard
                      key={p.id}
                      name={p.productName ?? "Unknown Product"}
                      shelfFrom={p.shelfFrom ?? "Unknown"}
                      isCompleted={p.status === ShelfChangeStatus.completed}
                      onClick={() => {
                        if (p.status !== ShelfChangeStatus.completed) return;
                        const url = generatePath(routes.inventory.shelfChanges.detail, {
                          proposalId: p.id,
                        });
                        navigate(url);
                      }}
                    />
                  ))}
                </Flex>
              </Flex>
            ))}
          </Flex>
          <Flex
            w="100%"
            h="80px"
            justify="center"
            align="center"
            px="s200"
            pos="fixed"
            gap="s100"
            bottom={0}
          >
            <TitleS>
              {intl.formatMessage({
                id: "pages.inventory.shelf-changes.scan-shelf-to-start",
              })}
            </TitleS>
            <ScanIcon h="24px" w="24px" />
          </Flex>
          {isItemNotFoundModalOpen && <ItemNotFoundModal onClose={handleItemNotFoundModalClose} />}
          {isShelfAlreadyAssignedModalOpen && (
            <ShelfAlreadyAssignedModal onClose={handleShelfAlreadyAssignedModalClose} />
          )}
        </>
      )}
    </Page>
  );
}
